import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getNatureOfGroups, getMasterGroupsByNature, getSubGroupsByGroupId, checkITCodeExist,
    getApprovalStatusIT, getAllItCodeDetails, saveITCode, editITCode,
} from '../../api/AccountsAPI/itCodeAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchNatureOfGroups = createAsyncThunk('itCode/fetchNatureOfGroups', async (_, { rejectWithValue }) => {
    try { const res = await getNatureOfGroups(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch nature of groups')); }
});

export const fetchMasterGroups = createAsyncThunk('itCode/fetchMasterGroups', async (natureGroupId, { rejectWithValue }) => {
    try { const res = await getMasterGroupsByNature(natureGroupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchSubGroups = createAsyncThunk('itCode/fetchSubGroups', async (groupId, { rejectWithValue }) => {
    try { const res = await getSubGroupsByGroupId(groupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub groups')); }
});

export const checkItCodeExists = createAsyncThunk('itCode/checkExists', async (itcode, { rejectWithValue }) => {
    try { const res = await checkITCodeExist(itcode); return (res?.Data || []).length > 0; } catch (e) { return rejectWithValue(err(e, 'Failed to check IT code')); }
});

export const fetchApprovalStatusIT = createAsyncThunk('itCode/fetchApprovalStatus', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusIT('IT Code', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const fetchItCodeGrid = createAsyncThunk('itCode/fetchGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllItCodeDetails(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch IT codes')); }
});

export const submitNewItCode = createAsyncThunk('itCode/submitNew', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveITCode(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save IT code');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save IT code')); }
});

export const submitEditItCode = createAsyncThunk('itCode/submitEdit', async (payload, { rejectWithValue }) => {
    try {
        const res = await editITCode(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to update IT code');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update IT code')); }
});

export const submitDeleteItCode = createAsyncThunk('itCode/submitDelete', async (payload, { rejectWithValue }) => {
    try {
        const res = await editITCode(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to delete IT code');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete IT code')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    natureGroups: [], natureGroupsLoading: false,
    masterGroups: [], masterGroupsLoading: false,
    subGroups: [], subGroupsLoading: false,
    exists: null, existsLoading: false,
    allowedActions: [], allowedActionsLoading: false,

    grid: [], gridLoading: false, gridError: null,

    saveLoading: false, saveResult: null, saveError: null,
    editLoading: false, editResult: null, editError: null,
    deleteLoading: false, deleteResult: null, deleteError: null,
};

const itCodeSlice = createSlice({
    name: 'itCode',
    initialState,
    reducers: {
        clearExists: (s) => { s.exists = null; },
        clearSaveResult: (s) => { s.saveResult = null; s.saveError = null; },
        clearEditResult: (s) => { s.editResult = null; s.editError = null; },
        clearDeleteResult: (s) => { s.deleteResult = null; s.deleteError = null; },
        clearMasterGroups: (s) => { s.masterGroups = []; s.subGroups = []; },
        clearSubGroups: (s) => { s.subGroups = []; },
        resetItCode: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNatureOfGroups.pending, (s) => { s.natureGroupsLoading = true; })
            .addCase(fetchNatureOfGroups.fulfilled, (s, a) => { s.natureGroupsLoading = false; s.natureGroups = a.payload; })
            .addCase(fetchNatureOfGroups.rejected, (s) => { s.natureGroupsLoading = false; });

        builder
            .addCase(fetchMasterGroups.pending, (s) => { s.masterGroupsLoading = true; })
            .addCase(fetchMasterGroups.fulfilled, (s, a) => { s.masterGroupsLoading = false; s.masterGroups = a.payload; })
            .addCase(fetchMasterGroups.rejected, (s) => { s.masterGroupsLoading = false; });

        builder
            .addCase(fetchSubGroups.pending, (s) => { s.subGroupsLoading = true; })
            .addCase(fetchSubGroups.fulfilled, (s, a) => { s.subGroupsLoading = false; s.subGroups = a.payload; })
            .addCase(fetchSubGroups.rejected, (s) => { s.subGroupsLoading = false; });

        builder
            .addCase(checkItCodeExists.pending, (s) => { s.existsLoading = true; })
            .addCase(checkItCodeExists.fulfilled, (s, a) => { s.existsLoading = false; s.exists = a.payload; })
            .addCase(checkItCodeExists.rejected, (s) => { s.existsLoading = false; s.exists = null; });

        builder
            .addCase(fetchApprovalStatusIT.pending, (s) => { s.allowedActionsLoading = true; })
            .addCase(fetchApprovalStatusIT.fulfilled, (s, a) => { s.allowedActionsLoading = false; s.allowedActions = a.payload; })
            .addCase(fetchApprovalStatusIT.rejected, (s) => { s.allowedActionsLoading = false; });

        builder
            .addCase(fetchItCodeGrid.pending, (s) => { s.gridLoading = true; s.gridError = null; })
            .addCase(fetchItCodeGrid.fulfilled, (s, a) => { s.gridLoading = false; s.grid = a.payload; })
            .addCase(fetchItCodeGrid.rejected, (s, a) => { s.gridLoading = false; s.gridError = a.payload; });

        builder
            .addCase(submitNewItCode.pending, (s) => { s.saveLoading = true; s.saveError = null; s.saveResult = null; })
            .addCase(submitNewItCode.fulfilled, (s, a) => { s.saveLoading = false; s.saveResult = a.payload; })
            .addCase(submitNewItCode.rejected, (s, a) => { s.saveLoading = false; s.saveError = a.payload; });

        builder
            .addCase(submitEditItCode.pending, (s) => { s.editLoading = true; s.editError = null; s.editResult = null; })
            .addCase(submitEditItCode.fulfilled, (s, a) => { s.editLoading = false; s.editResult = a.payload; })
            .addCase(submitEditItCode.rejected, (s, a) => { s.editLoading = false; s.editError = a.payload; });

        builder
            .addCase(submitDeleteItCode.pending, (s) => { s.deleteLoading = true; s.deleteError = null; s.deleteResult = null; })
            .addCase(submitDeleteItCode.fulfilled, (s, a) => { s.deleteLoading = false; s.deleteResult = a.payload; })
            .addCase(submitDeleteItCode.rejected, (s, a) => { s.deleteLoading = false; s.deleteError = a.payload; });
    },
});

export const {
    clearExists, clearSaveResult, clearEditResult, clearDeleteResult,
    clearMasterGroups, clearSubGroups, resetItCode,
} = itCodeSlice.actions;

export const selectItCode = (s) => s.itCode;

export default itCodeSlice.reducer;
